<template>
  <el-form
    size="small"
    label-position="top"
  >
    <el-row
      v-for="(row, rowIndex) in mandatoryRows"
      :key="`mandatory-${rowIndex}`"
      :gutter="20"
    >
      <el-col
        v-for="field in row"
        :key="field.name"
        :span="field.span || defaultSpan"
      >
        <el-form-item :label="$t(field.label)">
          <el-row :gutter="10">
            <el-col
              v-if="field.operators"
              :span="12"
            >
              <el-select
                v-model="operator[field.name]"
                class="width-100"
              >
                <el-option
                  v-for="option in field.operators"
                  :key="option.value"
                  :label="$t(option.label)"
                  :value="option.value"
                />
              </el-select>
            </el-col>
            <el-col :span="field.operators ? 12 : 24">
              <component
                :is="controlTag(field)"
                :ref="field.name"
                class="width-100"
                v-bind="controlProps(field)"
                v-on="controlListeners(field)"
                @keyup.enter.native="commitOnEnter(field)"
              >
                <el-option
                  v-for="option in optionsFor(field)"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                />
              </component>
            </el-col>
          </el-row>
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="20">
      <el-col :span="24">
        <el-form-item>
          <el-button
            type="primary"
            size="small"
            icon="el-icon-refresh"
            :loading="loading"
            :disabled="searchDisabled"
            @click="$emit('refresh')"
          >
            {{ $t('monitoringDashboard.refreshAll') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="$emit('clear')"
          >
            {{ $t('monitoringDashboard.clearFilters') }}
          </el-button>
          <el-button
            v-if="canExport"
            plain
            size="small"
            icon="el-icon-download"
            @click="$emit('export')"
          >
            {{ $t('monitoringDashboard.export') }}
          </el-button>
        </el-form-item>
      </el-col>
    </el-row>

    <!--
      Chips for every field, mandatory and collapsed alike, sit outside the
      collapse: a column filter's chip has to stay visible once the panel shuts.
      Each group repeats its field label, since two fields can produce chips
      reading the same thing.
    -->
    <el-row
      v-for="(row, rowIndex) in activeRows"
      :key="`tags-${rowIndex}`"
      :gutter="20"
    >
      <el-col
        v-for="field in row"
        :key="`tags-${field.name}`"
        :span="field.span || defaultSpan"
      >
        <el-form-item :label="$t(field.label)">
          <FilterTags
            :filter="{ name: field.name, type: field.type }"
            :filter-data="tagsFor(field)"
            @tagClosed="removeTag"
          />
        </el-form-item>
      </el-col>
    </el-row>

    <el-row
      v-if="columnFields.length"
      :gutter="20"
    >
      <el-col :span="24">
        <el-collapse v-model="openPanels">
          <el-collapse-item
            name="columnFilters"
            :title="$t('monitoringDashboard.filters.columnFilters')"
          >
            <el-row
              v-for="(row, rowIndex) in columnRows"
              :key="`column-${rowIndex}`"
              :gutter="20"
            >
              <el-col
                v-for="field in row"
                :key="field.name"
                :span="field.span || defaultSpan"
              >
                <el-form-item :label="$t(field.label)">
                  <el-row :gutter="10">
                    <el-col
                      v-if="field.operators"
                      :span="12"
                    >
                      <el-select
                        v-model="operator[field.name]"
                        class="width-100"
                      >
                        <el-option
                          v-for="option in field.operators"
                          :key="option.value"
                          :label="$t(option.label)"
                          :value="option.value"
                        />
                      </el-select>
                    </el-col>
                    <el-col :span="field.operators ? 12 : 24">
                      <component
                        :is="controlTag(field)"
                        :ref="field.name"
                        class="width-100"
                        v-bind="controlProps(field)"
                        v-on="controlListeners(field)"
                        @keyup.enter.native="commitOnEnter(field)"
                      >
                        <el-option
                          v-for="option in optionsFor(field)"
                          :key="option.value"
                          :label="option.label"
                          :value="option.value"
                        />
                      </component>
                    </el-col>
                  </el-row>
                </el-form-item>
              </el-col>
            </el-row>
          </el-collapse-item>
        </el-collapse>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
  import FilterTags from '@/components/FilterTags.vue';

  const GRID_SPANS = 24;
  const DEFAULT_SPAN = 6;

  const CONTROL_TAG = {
    dateRange: 'el-date-picker',
    toggle: 'el-switch',
    select: 'el-select',
    multiSelect: 'el-select',
    exclusiveSelect: 'el-select',
    autocomplete: 'el-select',
    number: 'el-input-number',
    text: 'el-input',
  };

  export default {
    name: 'MonitoringFilterBar',
    components: { FilterTags },
    props: {
      // Filter values keyed by field name. Chip-accumulating types hold an
      // array of { key, label }, dateRange holds ['from', 'to'], toggle a
      // boolean, exclusiveSelect a single option value.
      value: {
        type: Object,
        required: true,
      },
      // Field descriptors: { name, label, type, options, operators, span,
      // mandatory, required, placeholder, autocompleteSearch }. `mandatory`
      // keeps the field out of the collapse, `required` gates the search
      // button. `label` and every option label are full i18n keys.
      // `autocompleteSearch(query, cb)` backs the autocomplete type and owns
      // its own minimum-characters gate. `defaultValue` is the exclusiveSelect
      // option that reads as "no filter" and so grows no chip. `component`
      // replaces the type's default control.
      fields: {
        type: Array,
        default: () => [],
      },
      loading: {
        type: Boolean,
        default: false,
      },
      canExport: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        // Transient control values: a selection becomes a chip and the control
        // goes back to empty.
        model: {},
        operator: {},
        remoteOptions: {},
        remoteLoading: {},
        openPanels: [],
        defaultSpan: DEFAULT_SPAN,
      };
    },
    created() {
      this.fields.forEach((field) => {
        this.$set(this.model, field.name, null);
        if (field.operators) {
          this.$set(this.operator, field.name, field.operators[0].value);
        }
        if (field.type === 'autocomplete') {
          this.$set(this.remoteOptions, field.name, []);
          this.$set(this.remoteLoading, field.name, false);
        }
      });
    },
    watch: {
      // A filter that goes from set to empty has to clear its control too:
      // after Svuota filtri the remote dropdown would otherwise still list the
      // previous search's options. Only the set -> empty transition clears, so
      // a dropdown the user is typing into is left alone.
      value(current, previous) {
        if (!previous) return;
        this.fields.forEach((field) => {
          if (this.hasValueIn(current, field)) return;
          if (!this.hasValueIn(previous, field)) return;
          this.model[field.name] = null;
          if (field.type === 'autocomplete') {
            this.remoteOptions[field.name] = [];
          }
        });
      },
    },
    computed: {
      mandatoryRows() {
        return this.packRows(this.fields.filter((field) => field.mandatory));
      },
      columnFields() {
        return this.fields.filter((field) => !field.mandatory);
      },
      columnRows() {
        return this.packRows(this.columnFields);
      },
      activeRows() {
        return this.packRows(
          this.fields.filter((field) => this.tagsFor(field).length)
        );
      },
      searchDisabled() {
        return this.fields.some(
          (field) => field.required && !this.hasCompleteValue(field)
        );
      },
    },
    methods: {
      packRows(fields) {
        const rows = [];
        let row = [];
        let used = 0;
        fields.forEach((field) => {
          const span = field.span || DEFAULT_SPAN;
          if (used + span > GRID_SPANS) {
            rows.push(row);
            row = [];
            used = 0;
          }
          row.push(field);
          used += span;
        });
        if (row.length) rows.push(row);
        return rows;
      },
      controlTag(field) {
        return field.component || CONTROL_TAG[field.type] || 'el-input';
      },
      controlProps(field) {
        const placeholder = field.placeholder ? this.$t(field.placeholder) : '';
        switch (field.type) {
          case 'dateRange':
            return {
              value: this.value[field.name] || null,
              type: 'daterange',
              rangeSeparator: '-',
              format: 'dd-MM-yyyy',
              valueFormat: 'yyyy-MM-dd',
              startPlaceholder: this.$t('filters.startDate'),
              endPlaceholder: this.$t('filters.endDate'),
            };
          case 'toggle':
            return { value: !!this.value[field.name] };
          case 'autocomplete':
            return {
              value: this.model[field.name],
              filterable: true,
              remote: true,
              clearable: true,
              remoteMethod: (query) => this.searchRemote(field, query),
              loading: !!this.remoteLoading[field.name],
              placeholder,
            };
          case 'multiSelect':
            return {
              value: this.selectedKeys(field),
              multiple: true,
              collapseTags: true,
              clearable: true,
              placeholder,
            };
          case 'select':
            return {
              value: this.model[field.name],
              clearable: true,
              placeholder,
            };
          case 'exclusiveSelect':
            return {
              value: this.value[field.name],
              clearable: false,
              placeholder,
            };
          case 'number':
            // el-input-number coerces null to 0, so an empty control has to be
            // undefined. No min/max either: days remaining goes negative once
            // an analysis is overdue.
            return {
              value:
                this.model[field.name] === null
                  ? undefined
                  : this.model[field.name],
              controlsPosition: 'right',
              precision: 0,
              step: 1,
              placeholder,
            };
          default:
            return { value: this.model[field.name], type: 'text', placeholder };
        }
      },
      controlListeners(field) {
        const setModel = (controlValue) => {
          this.model[field.name] = controlValue;
        };
        switch (field.type) {
          case 'dateRange':
            return { input: (range) => this.setRange(field, range) };
          case 'toggle':
            return { change: (checked) => this.setToggle(field, checked) };
          case 'multiSelect':
            return { input: (keys) => this.setMultiSelect(field, keys) };
          case 'autocomplete':
            return {
              input: setModel,
              change: (selected) => this.onRemoteSelect(field, selected),
            };
          case 'select':
            return {
              input: setModel,
              change: (selected) => this.onSelect(field, selected),
            };
          case 'exclusiveSelect':
            return {
              change: (selected) => this.emitValue(field.name, selected),
            };
          case 'number':
            return {
              input: setModel,
              blur: () => this.onNumberChange(field, this.model[field.name]),
            };
          default:
            return {
              input: setModel,
              change: (text) => this.onTextChange(field, text),
            };
        }
      },
      optionsFor(field) {
        if (field.type === 'autocomplete') {
          const selected = new Set(this.selectedKeys(field));
          return (this.remoteOptions[field.name] || []).filter(
            (option) => !selected.has(option.value)
          );
        }
        if (field.type === 'multiSelect' || field.type === 'exclusiveSelect') {
          return (field.options || []).map((option) => ({
            value: option.value,
            label: this.$t(option.label),
          }));
        }
        if (field.type === 'select') {
          const selected = new Set(this.selectedKeys(field));
          return (field.options || [])
            .filter((option) => !selected.has(option.value))
            .map((option) => ({
              value: option.value,
              label: this.$t(option.label),
            }));
        }
        return [];
      },
      hasCompleteValue(field) {
        const current = this.value[field.name];
        if (field.type === 'dateRange') {
          return !!(
            current &&
            current.length === 2 &&
            current[0] &&
            current[1]
          );
        }
        if (field.type === 'toggle') return current !== undefined;
        return !!(current && current.length);
      },
      hasValueIn(values, field) {
        const current = values[field.name];
        if (field.type === 'dateRange') {
          return !!(current && current[0] && current[1]);
        }
        if (field.type === 'toggle') return !!current;
        if (field.type === 'exclusiveSelect') return false;
        return !!(current && current.length);
      },
      entries(field) {
        return this.value[field.name] || [];
      },
      selectedKeys(field) {
        return this.entries(field).map((entry) => entry.key);
      },
      tagsFor(field) {
        const current = this.value[field.name];
        if (field.type === 'dateRange') {
          if (!current || !current[0] || !current[1]) return [];
          return [
            {
              key: 'range',
              label: `${this.displayDate(current[0])} - ${this.displayDate(
                current[1]
              )}`,
            },
          ];
        }
        if (field.type === 'toggle') {
          return current ? [{ key: 'on', label: this.$t(field.label) }] : [];
        }
        if (field.type === 'exclusiveSelect') {
          if (!current || current === field.defaultValue) return [];
          const option = (field.options || []).find((o) => o.value === current);
          return [
            {
              key: current,
              label: option ? this.$t(option.label) : String(current),
            },
          ];
        }
        return current || [];
      },
      displayDate(isoDay) {
        const [year, month, day] = String(isoDay).split('-');
        return `${day}/${month}/${year}`;
      },
      emitValue(name, fieldValue) {
        this.$emit('input', { ...this.value, [name]: fieldValue });
      },
      addEntry(field, entry) {
        if (this.selectedKeys(field).includes(entry.key)) return;
        this.emitValue(field.name, [...this.entries(field), entry]);
      },
      removeTag({ name, item }) {
        const field = this.fields.find((f) => f.name === name);
        if (!field) return;
        if (field.type === 'dateRange') {
          this.emitValue(name, null);
          return;
        }
        if (field.type === 'toggle') {
          this.emitValue(name, false);
          return;
        }
        if (field.type === 'exclusiveSelect') {
          this.emitValue(name, field.defaultValue);
          return;
        }
        this.emitValue(
          name,
          this.entries(field).filter((entry) => entry.key !== item.key)
        );
      },
      setRange(field, range) {
        this.emitValue(field.name, range || null);
      },
      setToggle(field, checked) {
        this.emitValue(field.name, checked);
      },
      setMultiSelect(field, keys) {
        const byKey = new Map(
          (field.options || []).map((option) => [option.value, option])
        );
        this.emitValue(
          field.name,
          keys.map((key) => ({
            key,
            label: byKey.has(key) ? this.$t(byKey.get(key).label) : String(key),
          }))
        );
      },
      onSelect(field, selected) {
        const option = (field.options || []).find((o) => o.value === selected);
        if (!option) return;
        this.addEntry(field, {
          key: option.value,
          label: this.$t(option.label),
        });
        this.resetControl(field);
      },
      onTextChange(field, text) {
        if (!text) return;
        this.addEntry(field, { key: text, label: text });
        this.model[field.name] = null;
      },
      commitOnEnter(field) {
        if (field.type !== 'number') return;
        this.onNumberChange(field, this.model[field.name]);
      },
      onNumberChange(field, number) {
        if (number === '' || number === null || number === undefined) return;
        const operatorValue = this.operator[field.name] || null;
        const operatorOption = (field.operators || []).find(
          (o) => o.value === operatorValue
        );
        this.addEntry(field, {
          key: operatorValue ? `${operatorValue}:${number}` : String(number),
          label: operatorOption
            ? `${this.$t(operatorOption.label)} ${number}`
            : String(number),
          operator: operatorValue,
          value: number,
        });
        this.model[field.name] = null;
      },
      async searchRemote(field, query) {
        if (!field.autocompleteSearch) return;
        this.remoteLoading[field.name] = true;
        try {
          await field.autocompleteSearch(query, (results) => {
            this.remoteOptions[field.name] = results || [];
          });
        } finally {
          this.remoteLoading[field.name] = false;
        }
      },
      onRemoteSelect(field, selected) {
        const option = (this.remoteOptions[field.name] || []).find(
          (o) => o.value === selected
        );
        if (!option) return;
        this.addEntry(field, { key: option.value, label: option.label });
        this.resetControl(field);
      },
      resetControl(field) {
        this.model[field.name] = null;
        // `ref` inside v-for collects an array.
        const refs = [].concat(this.$refs[field.name] || []);
        if (refs[0]) setTimeout(() => refs[0].blur());
      },
    },
  };
</script>

<style lang="scss" scoped>
  ::v-deep .el-input-number {
    width: 100%;
  }
</style>
